// src/components/Public/Stream.js
import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../hooks/useAuth';
import SEO from '../SEO';

const Stream = () => {
  const [channelName, setChannelName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchChannelName();
  }, []);

  const fetchChannelName = async () => {
    try {
      const streamDoc = await getDoc(doc(db, 'settings', 'stream'));
      if (streamDoc.exists()) {
        setChannelName(streamDoc.data().channelName);
      }
    } catch (error) {
      console.error('Error fetching stream settings:', error);
      setError('Failed to load stream settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChannel = async (newChannel) => {
    try {
      await setDoc(doc(db, 'settings', 'stream'), {
        channelName: newChannel,
        updatedAt: new Date(),
        updatedBy: user?.email,
      });
      setChannelName(newChannel);
      setIsEditing(false);
      setError(null);
    } catch (error) {
      console.error('Error saving stream settings:', error);
      setError('Failed to save stream settings');
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <SEO
        title="Live Stream — Watch Competitive KZ"
        description="Watch the official KZCOMP live stream — competitive KZ matches, runs and tournament broadcasts on Twitch."
        canonicalPath="/stream"
      />
      <Paper
        sx={{ p: 3 }}
        style={{
          backgroundColor: '#123',
          color: '#fff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h4" component="h1">
            Live Stream
          </Typography>
          {user && (
            <Tooltip title="Configure Stream">
              <IconButton
                onClick={() => setIsEditing(!isEditing)}
                color={isEditing ? 'primary' : 'default'}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isEditing ? (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Twitch Channel Name"
              defaultValue={channelName}
              size="small"
              sx={{ mb: 1 }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSaveChannel(e.target.value);
                }
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => handleSaveChannel(document.querySelector('input').value)}
              >
                Save
              </Button>
              <Button variant="outlined" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </Box>
          </Box>
        ) : !channelName ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No stream channel has been configured yet.
            {user && ' Click the settings icon to set up the stream.'}
          </Alert>
        ) : null}

        {channelName && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              paddingTop: '56.25%', // 16:9 Aspect Ratio
            }}
          >
            <iframe
              src={`https://player.twitch.tv/?channel=${channelName}&parent=${window.location.hostname}`}
              frameBorder="0"
              allowFullScreen
              scrolling="no"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              title="Twitch Stream"
            />
          </Box>
        )}

        {channelName && (
          <Box sx={{ mt: 2 }}>
            <iframe
              src={`https://www.twitch.tv/embed/${channelName}/chat?parent=${window.location.hostname}`}
              height="500"
              width="100%"
              frameBorder="0"
              scrolling="no"
              title="Twitch Chat"
            />
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Stream;
